/**
 * pinField — one spelling of "this box holds a PIN, not a password".
 *
 * WHY THIS EXISTS (owner report 2026-09-23): changing a price at /sales-new and
 * confirming at checkout made Chrome offer to SAVE the PIN, and password
 * managers offered to fill it.
 *
 * The app had 132 PIN boxes across 30+ files, each hand-written. 23 carried no
 * guard at all (including the price-override box the owner hit); the other 109
 * carried `autocomplete="one-time-code"` + `data-lpignore`.
 *
 * Those guards were aimed at the wrong half of the problem. `autocomplete`
 * governs FILLING. Chrome's "Save password?" bubble is driven by the field
 * being `type="password"` in the first place — no attribute switches it off.
 * So the fix is to stop declaring a PIN as a password: a numeric text field,
 * masked in CSS, which no browser or manager classifies as a credential.
 *
 * SAFETY FALLBACK: if the browser cannot do CSS text masking, we go back to
 * `type="password"`. A visible PIN on screen would be a worse bug than a save
 * prompt, so the fallback always wins.
 *
 * Usage — spread onto ANY input, plain or styled:
 *   <input {...pinFieldProps()} value={pin} onChange={...} className="..." />
 *   <Input {...pinFieldProps({ maxLength: 8 })} value={pin} onChange={...} />
 */

// Feature-detected once. Chromium, Safari and Firefox 118+ support it; anything
// that does not falls back to a real password field.
let _canMask = null;
export function canMaskText() {
  if (_canMask !== null) return _canMask;
  try {
    _canMask =
      typeof window !== 'undefined' &&
      typeof window.CSS !== 'undefined' &&
      typeof window.CSS.supports === 'function' &&
      (window.CSS.supports('-webkit-text-security', 'disc') ||
        window.CSS.supports('text-security', 'disc'));
  } catch {
    _canMask = false;
  }
  return _canMask;
}

/**
 * @param {object} extra
 *   style      — merged after the masking style, so a caller can still override
 *   maxLength  — defaults to 8 (the longest PIN/TOTP this app accepts)
 *   name       — defaults to a neutral name; never anything password-shaped,
 *                because managers match on the name too
 *   ...rest    — passed straight through
 */
export function pinFieldProps(extra = {}) {
  const { style, maxLength = 8, name = 'verification-code', ...rest } = extra;
  const masked = canMaskText();

  return {
    // A PIN is a short numeric code, not a credential. Only fall back to a
    // password field where masking is impossible.
    type: masked ? 'text' : 'password',
    inputMode: 'numeric',
    maxLength,
    name,
    // Belt and braces for the browsers' own autofill, on top of not being a
    // password field any more.
    autoComplete: 'one-time-code',
    autoCorrect: 'off',
    autoCapitalize: 'off',
    spellCheck: false,
    // Password managers each read their own opt-out. The app previously had
    // only LastPass's, on 102 of 132 boxes, and none of the other three.
    'data-lpignore': 'true',        // LastPass
    'data-1p-ignore': 'true',       // 1Password
    'data-bwignore': 'true',        // Bitwarden
    'data-form-type': 'other',      // Dashlane
    style: masked
      ? { WebkitTextSecurity: 'disc', textSecurity: 'disc', ...(style || {}) }
      : style,
    ...rest,
  };
}

export default pinFieldProps;
